import React, { useState } from "react";
import { MapPin, Phone, Mail, Clock, Send, CheckCircle2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { useToast } from "../hooks/use-toast";
import { API_BASE } from "../DataContext";
import { usePageMeta } from "../hooks/usePageMeta";
import JsonLd from "../components/JsonLd";
import { buildWebPageLD, buildBreadcrumbLD } from "../lib/jsonld";

const OFFICES = [
  { city: "Kolkata (HQ)", addr: "39 Strand Road, Room No. 25, Kolkata 700001", phone: "1800 202 3360" },
  { city: "Howrah (Unit 1)", addr: "Vill & P. O. Lakshamanpur, P. S. Domjur, Howrah, West Bengal 711114", phone: "1800 202 3360" },
  { city: "Hooghly (Unit 2)", addr: "49 TN Mukherjee Road, Dankuni, Hooghly, West Bengal 712245", phone: "1800 202 3360" }
];

export default function Contact() {
  usePageMeta(
    "Contact Us — Enquiries & Dealer Support",
    "Get in touch with Baishalii Tanks & Pipes for product enquiries, dealership opportunities and support. Toll-free 1800 202 3360."
  );
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "" });
  const [submitted, setSubmitted] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const onSubmit = async (e) => {
    e.preventDefault();
    try {
      const res = await fetch(`${API_BASE}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      if (!res.ok) throw new Error("failed");
      setSubmitted(true);
      toast({ title: "Enquiry submitted", description: "Our team will reach out within 24 hours." });
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      setTimeout(() => setSubmitted(false), 4000);
    } catch {
      toast({ title: "Submission failed", description: "Please try again later.", variant: "destructive" });
    }
  };

  return (
    <div className="pt-[88px] md:pt-[112px]">
      <JsonLd blocks={[
        buildWebPageLD({ name: "Contact BAISHALII", path: "/contact", description: "Get in touch with Baishalii Tanks & Pipes for product enquiries, dealership opportunities and support." }),
        buildBreadcrumbLD([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]),
      ]} id="contact-page" />
      {/* hero */}
      <section className="relative py-24 bg-gradient-to-r from-[#061a3d] via-[#0a2a5e] to-sky-700 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10" style={{backgroundImage:"radial-gradient(circle at 30% 30%, white 1px, transparent 1px)", backgroundSize:"24px 24px"}}/>
        <div className="relative max-w-7xl mx-auto px-6 text-center">
          <div className="text-sky-300 font-semibold text-sm tracking-[0.2em] mb-3">GET IN TOUCH</div>
          <h1 className="text-5xl md:text-6xl font-black mb-4">We'd love to hear from you</h1>
          <p className="text-slate-200 text-lg max-w-2xl mx-auto">Questions about products, partnerships or careers? Reach out and our team will respond within one working day.</p>
        </div>
      </section>

      {/* quick info */}
      <section className="py-12 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid md:grid-cols-3 gap-5 -mt-24 relative z-10">
          {[
            { Icon: Phone, title: "Call us", lines: ["Toll-free: 1800 202 3360"] },
            { Icon: Mail, title: "Email us", lines: ["contact@baishalii.in"] },
            { Icon: Clock, title: "Working hours", lines: ["Mon – Sat: 9:30 AM – 6:30 PM", "Sun: Closed"] }
          ].map(({ Icon, title, lines }, i) => (
            <div key={i} className="bg-white p-7 rounded-2xl shadow-xl border border-slate-100 hover:-translate-y-1 transition-transform">
              <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-sky-500 to-[#0a2a5e] flex items-center justify-center mb-4">
                <Icon className="h-6 w-6 text-white" />
              </div>
              <h3 className="font-black text-[#0a2a5e] text-lg mb-2">{title}</h3>
              {lines.map((l, j) => <p key={j} className="text-slate-600 text-sm">{l}</p>)}
            </div>
          ))}
        </div>
      </section>

      {/* form + map */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 grid lg:grid-cols-2 gap-10">
          <div className="bg-white p-8 md:p-10 rounded-2xl shadow-lg border border-slate-100">
            <h2 className="text-3xl font-black text-[#0a2a5e] mb-2">Send us a message</h2>
            <p className="text-slate-500 mb-7">Fill out the form and our team will get back within 24 hours.</p>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Full Name *</label>
                  <Input required value={form.name} onChange={onChange("name")} placeholder="John Doe" data-testid="contact-name-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Email *</label>
                  <Input required type="email" value={form.email} onChange={onChange("email")} placeholder="you@company.com" data-testid="contact-email-input" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Phone</label>
                  <Input value={form.phone} onChange={onChange("phone")} placeholder="+91 98765 43210" data-testid="contact-phone-input" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Subject *</label>
                  <Select value={form.subject} onValueChange={(v) => setForm({ ...form, subject: v })}>
                    <SelectTrigger data-testid="contact-subject-select"><SelectValue placeholder="Choose a topic" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="product">Product Enquiry</SelectItem>
                      <SelectItem value="dealer">Dealership</SelectItem>
                      <SelectItem value="career">Career</SelectItem>
                      <SelectItem value="support">Support</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-600 mb-1.5 block">Message *</label>
                <Textarea required rows={5} value={form.message} onChange={onChange("message")} placeholder="Tell us about your project or requirement..." data-testid="contact-message-textarea" />
              </div>
              <Button type="submit" disabled={submitted} data-testid="contact-submit-btn" className="w-full bg-gradient-to-r from-sky-500 to-[#0a2a5e] hover:opacity-90 text-white rounded-full h-12 font-semibold">
                {submitted ? (<><CheckCircle2 className="h-4 w-4 mr-1.5" /> Submitted</>) : (<><Send className="h-4 w-4 mr-1.5" /> Send Message</>)}
              </Button>
            </form>
          </div>

          <div>
            <h2 className="text-3xl font-black text-[#0a2a5e] mb-6">Visit our offices</h2>
            <div className="space-y-4 mb-6">
              {OFFICES.map((o, i) => (
                <div key={i} className="bg-white p-5 rounded-xl border border-slate-200 flex gap-4 hover:shadow-md transition-shadow">
                  <div className="h-10 w-10 rounded-lg bg-sky-100 flex items-center justify-center shrink-0">
                    <MapPin className="h-5 w-5 text-sky-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-[#0a2a5e] mb-1">{o.city}</h3>
                    <p className="text-sm text-slate-600 leading-snug mb-1">{o.addr}</p>
                    <a href={`tel:${o.phone}`} className="text-sm text-sky-600 font-semibold hover:underline">{o.phone}</a>
                  </div>
                </div>
              ))}
            </div>
            <div className="rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
              <iframe
                title="BAISHALII Map"
                src="https://www.openstreetmap.org/export/embed.html?bbox=88.42%2C22.56%2C88.45%2C22.59&layer=mapnik"
                className="w-full h-72"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
